'use client';

import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import {
  MARKETING_PAIR_BLOCK_SX,
  MARKETING_PAIR_IMAGE_SX,
} from '@/components/website/Marketing/MarketingIndexChrome';
import MarketingReveal from '@/components/website/Marketing/MarketingReveal';
import {
  MARKETING,
  MARKETING_BODY_SX,
  MARKETING_EYEBROW_SX,
  MARKETING_HEADING_SX,
} from '@/components/website/Marketing/marketing-tokens';
import MarketingSectionImage from '@/components/website/MarketingPage/MarketingSectionImage';

export const PROCESS_SUBHEAD_SX = {
  m: 0,
  fontFamily: MARKETING.serif,
  fontWeight: 300,
  fontSize: { xs: '22px', md: '26px' },
  lineHeight: 1.25,
  letterSpacing: '-0.01em',
  color: MARKETING.text,
};

export const PROCESS_STACK_SX = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
};

const LEAD_MARK_SX = {
  display: 'inline',
  m: 0,
  py: '0.12em',
  px: '0.28em',
  ml: '-0.28em',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
  bgcolor: 'rgba(184, 173, 154, 0.22)',
  color: MARKETING.text,
  borderRadius: '2px',
};

export const PROCESS_PAIR_STAGE_SX = {
  ...MARKETING_PAIR_BLOCK_SX,
  alignItems: 'stretch',
};

export const PROCESS_APPROVALS_STAGE_PAPER_SX = {
  bgcolor: MARKETING.ink,
  backgroundImage: 'none',
  color: MARKETING.text,
  border: `1px solid ${MARKETING.hairline}`,
  borderRadius: 0,
  boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
  p: { xs: '16px', md: '24px' },
  display: { md: 'flex' },
  flexDirection: 'column',
  alignSelf: 'stretch',
  minHeight: 0,
  '& > *': {
    flex: { md: 1 },
    minHeight: { md: 0 },
    height: { md: '100%' },
    width: '100%',
  },
};

export const PROCESS_PAIR_STAGE_SINGLE_IMAGE_SX = {
  ...MARKETING_PAIR_IMAGE_SX,
  height: { xs: 240, sm: 300, md: '100%' },
  minHeight: { md: 360 },
};

export const PROCESS_APPROVALS_GRID_IMAGE_SX = {
  position: 'relative',
  width: '100%',
  height: { xs: 108, sm: 132, md: '100%' },
  minHeight: { xs: 108, sm: 132, md: 160 },
  display: 'block',
  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.45)',
  alignSelf: 'stretch',
};

export const PROCESS_PRODUCTION_WARNING_SX = {
  ...MARKETING_BODY_SX,
  maxWidth: 'none',
  fontStyle: 'italic',
  color: '#E85A4F',
};

export function ProcessPairCopy({ eyebrow, heading, children, imageSrc, imageAlt, objectFit }) {
  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', order: { md: 1 } }}>
        {eyebrow ? (
          <MarketingReveal observe={false} delay={0}>
            <Typography sx={MARKETING_EYEBROW_SX}>{eyebrow}</Typography>
          </MarketingReveal>
        ) : null}
        <MarketingReveal observe={false} delay={0.15}>
          <Typography component="h2" sx={MARKETING_HEADING_SX}>
            {heading}
          </Typography>
        </MarketingReveal>
        <MarketingReveal observe={false} delay={0.3}>
          <Box sx={PROCESS_STACK_SX}>{children}</Box>
        </MarketingReveal>
      </Box>
      {imageSrc ? (
        <MarketingReveal
          observe={false}
          delay={0.5}
          from="right"
          sx={{ width: '100%', order: { xs: -1, md: 2 } }}
        >
          <MarketingSectionImage
            src={imageSrc}
            alt={imageAlt}
            sizes="(max-width: 899px) 100vw, 50vw"
            sx={MARKETING_PAIR_IMAGE_SX}
            {...(objectFit === 'contain' ? { objectFit: 'contain' } : {})}
          />
        </MarketingReveal>
      ) : null}
    </>
  );
}

ProcessPairCopy.propTypes = {
  eyebrow: PropTypes.string,
  heading: PropTypes.string.isRequired,
  children: PropTypes.node,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
  objectFit: PropTypes.string,
};

export function ProcessPairStage({ copy, media }) {
  return (
    <Box sx={PROCESS_PAIR_STAGE_SX}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', order: { md: 1 } }}>
        <MarketingReveal observe={false} delay={0.3}>
          <Box sx={PROCESS_STACK_SX}>{copy}</Box>
        </MarketingReveal>
      </Box>
      <MarketingReveal
        observe={false}
        delay={0.5}
        from="right"
        sx={{
          width: '100%',
          height: { md: '100%' },
          minHeight: 0,
          display: { md: 'flex' },
          flexDirection: 'column',
          order: { xs: -1, md: 2 },
          '& > *': {
            flex: { md: 1 },
            minHeight: { md: 0 },
            height: { md: '100%' },
          },
        }}
      >
        {media}
      </MarketingReveal>
    </Box>
  );
}

ProcessPairStage.propTypes = {
  copy: PropTypes.node,
  media: PropTypes.node,
};

export function ProcessPairImageGrid({ images }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridAutoRows: { xs: 'auto', md: '1fr' },
        alignItems: 'stretch',
        gap: '10px',
        height: { md: '100%' },
        minHeight: 0,
      }}
    >
      {images.map(({ src, alt, objectFit }) => (
        <MarketingSectionImage
          key={src}
          src={src}
          alt={alt}
          sizes="(max-width: 899px) 50vw, 25vw"
          sx={PROCESS_APPROVALS_GRID_IMAGE_SX}
          {...(objectFit === 'contain' ? { objectFit: 'contain' } : {})}
        />
      ))}
    </Box>
  );
}

ProcessPairImageGrid.propTypes = {
  images: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string.isRequired,
      alt: PropTypes.string.isRequired,
      objectFit: PropTypes.string,
    }),
  ).isRequired,
};

export function ProcessDetailList({ items }) {
  return (
    <Box
      component="ul"
      sx={{
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'flex',
        flexDirection: 'column',
        gap: '18px',
      }}
    >
      {items.map(item => (
        <Box component="li" key={item.title} sx={PROCESS_STACK_SX}>
          <Typography
            sx={{
              m: 0,
              fontFamily: MARKETING.sans,
              fontWeight: 500,
              fontSize: '12px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: MARKETING.taupe,
            }}
          >
            {item.title}
          </Typography>
          <Typography sx={{ ...MARKETING_BODY_SX, maxWidth: 'none' }}>
            {item.lead ? (
              <>
                <Box component="mark" sx={LEAD_MARK_SX}>
                  {item.lead}
                </Box>{' '}
                {item.body}
              </>
            ) : (
              item.body
            )}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

ProcessDetailList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      lead: PropTypes.string,
      body: PropTypes.string.isRequired,
    }),
  ).isRequired,
};

export function ProcessTimelinePhase({ title, eyebrow, children }) {
  return (
    <Box
      sx={{
        ...PROCESS_STACK_SX,
        gap: '18px',
      }}
    >
      <Typography component="h3" sx={PROCESS_SUBHEAD_SX}>
        {title}
      </Typography>
      {eyebrow ? <Typography sx={MARKETING_BODY_SX}>{eyebrow}</Typography> : null}
      {children}
    </Box>
  );
}

ProcessTimelinePhase.propTypes = {
  title: PropTypes.string.isRequired,
  eyebrow: PropTypes.string,
  children: PropTypes.node,
};
